package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*
import se.mittpass.shared.api.PayrollExportSettingsDto

import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*

private val fortnoxScope = MainScope()

private data class PayrollSystemPreset(
    val id: String,
    val name: String,
    val subtitle: String,
    val icon: String,
    val defaultFormat: String,
    val defaultEmployeeIdMode: String,
    val defaultCodeMode: String,
    val defaultCodeNormal: String,
    val defaultCodeOb1: String,
    val defaultCodeOb2: String,
    val defaultCodeOb3: String,
    val steps: (String) -> List<String>
)

private val payrollPresets = listOf(
    PayrollSystemPreset(
        id = "fortnox",
        name = "Fortnox Lön",
        subtitle = "PAXml 2.2 med tidkoder (TID, OB1-OB3) eller CSV",
        icon = "fa-building-columns",
        defaultFormat = "paxml",
        defaultEmployeeIdMode = "personal_number",
        defaultCodeMode = "tidkod",
        defaultCodeNormal = "TID",
        defaultCodeOb1 = "OB1",
        defaultCodeOb2 = "OB2",
        defaultCodeOb3 = "OB3",
        steps = { fmt ->
            if (fmt == "paxml") listOf(
                "Klicka på exportknappen ovan för att ladda ner PAXml-filen (.xml).",
                "Logga in i Fortnox och gå till Meny -> Lön -> Kalender eller Lönekörning.",
                "Klicka på \"Importera\" uppe till höger och välj PAXml-filen.",
                "Kontrollera att anställdas ID och tidkoder (TID, OB1-OB3) matchar och bekräfta importen."
            ) else listOf(
                "Ladda ner exportfilen ovan.",
                "Logga in i Fortnox -> Lön -> Tidrapporter.",
                "Klicka på \"Importera\" och välj den nedladdade filen.",
                "Mappa kolumnerna och verifiera importen."
            )
        }
    ),
    PayrollSystemPreset(
        id = "visma_lon",
        name = "Visma Lön 300 / 600",
        subtitle = "PAXml 2.2 med lönearter (1101, 1221-1223) och anställningsnummer",
        icon = "fa-file-invoice-dollar",
        defaultFormat = "paxml",
        defaultEmployeeIdMode = "employee_number",
        defaultCodeMode = "loneart",
        defaultCodeNormal = "1101",
        defaultCodeOb1 = "1221",
        defaultCodeOb2 = "1222",
        defaultCodeOb3 = "1223",
        steps = { _ ->
            listOf(
                "Exportera underlaget ovan som PAXml (.xml).",
                "Öppna Visma Lön 300/600 och gå till Lönearbete -> Lönekörning.",
                "Välj Arkiv -> Importera -> Löneunderlag (PAXml).",
                "Säkerställ att anställningsnummer och lönearter (t.ex. 1101 Timlön, 1221-1223 OB) stämmer mot ert löneregister."
            )
        }
    ),
    PayrollSystemPreset(
        id = "kontek_hogia",
        name = "Kontek / Hogia / Crona Lön",
        subtitle = "PAXml 2.2 med lönearter (010, 310-330) och anställningsnummer",
        icon = "fa-briefcase",
        defaultFormat = "paxml",
        defaultEmployeeIdMode = "employee_number",
        defaultCodeMode = "loneart",
        defaultCodeNormal = "010",
        defaultCodeOb1 = "310",
        defaultCodeOb2 = "320",
        defaultCodeOb3 = "330",
        steps = { _ ->
            listOf(
                "Ladda ner PAXml-filen (.xml) via exportknappen ovan.",
                "Öppna Kontek Lön, Hogia Lön eller Crona Lön och gå till Inläsning av tid/löneunderlag.",
                "Välj PAXml 2.2 som inläsningsformat och peka ut den nedladdade XML-filen.",
                "Granska importloggen och kontrollera att lönearter (t.ex. 010, 310-330) mappats rätt."
            )
        }
    ),
    PayrollSystemPreset(
        id = "bokio_bl",
        name = "Bokio / Björn Lundén / Spiris",
        subtitle = "Svensk CSV (; & decimalkomma) eller PAXml 2.2",
        icon = "fa-table-list",
        defaultFormat = "csv_se",
        defaultEmployeeIdMode = "personal_number",
        defaultCodeMode = "tidkod",
        defaultCodeNormal = "TID",
        defaultCodeOb1 = "OB1",
        defaultCodeOb2 = "OB2",
        defaultCodeOb3 = "OB3",
        steps = { fmt ->
            if (fmt == "paxml") listOf(
                "Ladda ner PAXml-filen (.xml) ovan.",
                "I Björn Lundén / Spiris (Visma Lön Smart): gå till Lön -> Importera tidunderlag (PAXml).",
                "Välj filen och granska raderna innan du skapar lönebeskeden."
            ) else listOf(
                "Ladda ner den svenskformaterade CSV-filen (; och decimalkomma) ovan.",
                "Öppna Bokio eller ditt löneprogram (eller Excel) för lönekörning.",
                "Använd summerade timmar för ordinarie tid samt OB1-OB3 per anställd i lönekörningen."
            )
        }
    ),
    PayrollSystemPreset(
        id = "generic",
        name = "Generell CSV / JSON",
        subtitle = "Rådata i CSV eller JSON för eget system, BI eller revisorsunderlag",
        icon = "fa-file-code",
        defaultFormat = "csv",
        defaultEmployeeIdMode = "personal_number",
        defaultCodeMode = "tidkod",
        defaultCodeNormal = "TID",
        defaultCodeOb1 = "OB1",
        defaultCodeOb2 = "OB2",
        defaultCodeOb3 = "OB3",
        steps = { fmt ->
            listOf(
                "Välj önskat filformat (${fmt.uppercase()}) och klicka på exportknappen ovan.",
                "Filen innehåller samtliga pass i perioden uppdelat på ordinarie tid, rast, Kvälls-OB, Natt-OB och Helg-OB.",
                "Importera filen direkt i ert kalkylark, BI-verktyg eller externa lönesystem."
            )
        }
    )
)

private val formatOptions = listOf(
    "paxml" to "PAXml 2.2 (.xml)",
    "csv_se" to "CSV Svensk (; & komma)",
    "csv" to "CSV Standard (, & punkt)",
    "json" to "JSON (.json)"
)

@Composable
fun FortnoxExportPage(workplaceId: Long) {
    var fromDate by remember { mutableStateOf(currentMonthStartFortnox()) }
    var toDate by remember { mutableStateOf(currentTodayFortnox()) }

    var settings by remember { mutableStateOf(PayrollExportSettingsDto(workplaceId = workplaceId)) }
    var loadingSettings by remember { mutableStateOf(true) }
    var savingSettings by remember { mutableStateOf(false) }
    var exporting by remember { mutableStateOf(false) }
    var showAdvanced by remember { mutableStateOf(false) }
    var feedbackMsg by remember { mutableStateOf<String?>(null) }
    var feedbackIsError by remember { mutableStateOf(false) }

    LaunchedEffect(workplaceId) {
        loadingSettings = true
        fortnoxRepo.getExportSettings(workplaceId).onSuccess { loaded ->
            settings = loaded
        }
        loadingSettings = false
    }

    val activePreset = payrollPresets.find { it.id == settings.targetSystem } ?: payrollPresets.first()
    val activeFormatLabel = formatOptions.find { it.first == settings.fileFormat }?.second ?: settings.fileFormat.uppercase()

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.25rem") } }) {

        feedbackMsg?.let { msg ->
            Div({
                style {
                    property("background", if (!feedbackIsError) "var(--rp-ok-bg)" else "var(--rp-warn-bg)")
                    property("color", if (!feedbackIsError) "var(--rp-ok-text)" else "var(--rp-warn-text)")
                    property("padding", "0.75rem 1.25rem"); property("border-radius", "14px"); property("font-weight", "600")
                    property("display", "flex"); property("align-items", "center"); property("gap", "0.6rem")
                }
            }) {
                I({ classes("fa-solid", if (!feedbackIsError) "fa-circle-check" else "fa-triangle-exclamation") })
                Text(msg)
            }
        }

        // Main export card
        Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.5rem") } }) {
            Div({
                style {
                    property("display", "flex"); property("justify-content", "space-between")
                    property("align-items", "flex-start"); property("flex-wrap", "wrap"); property("gap", "1rem")
                    property("margin-bottom", "1.25rem")
                }
            }) {
                Div {
                    H3({ style { property("margin", "0 0 0.35rem 0"); property("font-size", "1.3rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                        Text("Löneexport")
                    }
                    P({ style { property("margin", "0"); property("color", "var(--rp-muted)"); property("font-size", "0.9rem") } }) {
                        Text("Välj ert lönesystem, spara som förval för arbetsplatsen och exportera färdigt löneunderlag.")
                    }
                }

                // Save default settings button
                Button({
                    onClick {
                        savingSettings = true
                        feedbackMsg = null
                        fortnoxScope.launch {
                            fortnoxRepo.saveExportSettings(workplaceId, settings).onSuccess { saved ->
                                settings = saved
                                feedbackIsError = false
                                feedbackMsg = "Exportprofil sparad som förval för arbetsplatsen (${activePreset.name})."
                            }.onFailure {
                                feedbackIsError = true
                                feedbackMsg = "Kunde inte spara inställningar: ${it.message}"
                            }
                            savingSettings = false
                        }
                    }
                    if (savingSettings || loadingSettings) disabled()
                    style {
                        property("padding", "0.55rem 1.1rem")
                        property("border", "2px solid var(--rp-pine)")
                        property("background", "transparent")
                        property("color", "var(--rp-pine)")
                        property("border-radius", "50px")
                        property("font-weight", "700")
                        property("font-size", "0.85rem")
                        property("cursor", "pointer")
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("gap", "0.45rem")
                        if (savingSettings) property("opacity", "0.7")
                    }
                }) {
                    I({ classes("fa-solid", "fa-floppy-disk") })
                    Text(if (savingSettings) "Sparar..." else "Spara som förval")
                }
            }

            // 1. Target system selector grid
            Span({ style { property("display", "block"); property("font-weight", "700"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem"); property("margin-bottom", "0.6rem") } }) {
                Text("1. Välj målsystem")
            }
            Div({
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(auto-fit, minmax(210px, 1fr))")
                    property("gap", "0.75rem")
                    property("margin-bottom", "1.5rem")
                }
            }) {
                payrollPresets.forEach { preset ->
                    val isSelected = settings.targetSystem == preset.id
                    Div({
                        onClick {
                            settings = settings.copy(
                                targetSystem = preset.id,
                                fileFormat = preset.defaultFormat,
                                employeeIdMode = preset.defaultEmployeeIdMode,
                                codeMode = preset.defaultCodeMode,
                                codeNormal = preset.defaultCodeNormal,
                                codeOb1 = preset.defaultCodeOb1,
                                codeOb2 = preset.defaultCodeOb2,
                                codeOb3 = preset.defaultCodeOb3
                            )
                        }
                        style {
                            property("padding", "0.9rem 1rem")
                            property("border-radius", "16px")
                            property("border", if (isSelected) "2px solid var(--rp-pine)" else "2px solid var(--rp-hl-med)")
                            property("background", if (isSelected) "var(--rp-info-bg)" else "var(--rp-base)")
                            property("cursor", "pointer")
                            property("transition", "all 0.15s ease")
                        }
                    }) {
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.55rem"); property("margin-bottom", "0.3rem") } }) {
                            I({
                                classes("fa-solid", preset.icon)
                                style { property("color", if (isSelected) "var(--rp-pine)" else "var(--rp-muted)") }
                            })
                            Span({ style { property("font-weight", "800"); property("font-size", "0.95rem"); property("color", "var(--rp-text)") } }) {
                                Text(preset.name)
                            }
                        }
                        P({ style { property("margin", "0"); property("font-size", "0.78rem"); property("color", "var(--rp-muted)"); property("line-height", "1.35") } }) {
                            Text(preset.subtitle)
                        }
                    }
                }
            }

            // 2. Period & Format row
            Div({
                style {
                    property("display", "flex"); property("flex-wrap", "wrap"); property("align-items", "flex-end")
                    property("gap", "1.25rem"); property("margin-bottom", "1.25rem")
                }
            }) {
                // Period
                Div {
                    Span({ style { property("display", "block"); property("font-weight", "700"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem"); property("margin-bottom", "0.4rem") } }) {
                        Text("2. Period")
                    }
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem"); property("flex-wrap", "wrap") } }) {
                        Input(InputType.Date) {
                            value(fromDate); onInput { fromDate = it.value }
                            style { fortnoxInputStyle() }
                        }
                        Span({ style { property("color", "var(--rp-muted)") } }) { Text("\u2013") }
                        Input(InputType.Date) {
                            value(toDate); onInput { toDate = it.value }
                            style { fortnoxInputStyle() }
                        }
                    }
                }

                // Format selector
                Div {
                    Span({ style { property("display", "block"); property("font-weight", "700"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem"); property("margin-bottom", "0.4rem") } }) {
                        Text("3. Exportformat")
                    }
                    Select({
                        onChange { ev ->
                            ev.target.value.takeIf { it.isNotBlank() }?.let { fmt ->
                                settings = settings.copy(fileFormat = fmt)
                            }
                        }
                        style { fortnoxInputStyle() }
                    }) {
                        formatOptions.forEach { (fmtKey, fmtLabel) ->
                            Option(fmtKey, {
                                if (settings.fileFormat == fmtKey) selected()
                            }) { Text(fmtLabel) }
                        }
                    }
                }

                // Only approved toggle
                Div({ style { property("display", "flex"); property("align-items", "center"); property("padding-bottom", "0.45rem") } }) {
                    Label(attrs = {
                        style {
                            property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.5rem")
                            property("font-size", "0.88rem"); property("font-weight", "600"); property("color", "var(--rp-text)"); property("cursor", "pointer")
                        }
                    }) {
                        Input(InputType.Checkbox) {
                            checked(settings.onlyApproved)
                            onChange { settings = settings.copy(onlyApproved = it.value) }
                        }
                        Text("Endast godkända pass")
                    }
                }
            }

            // Toggle for code customization
            Div({ style { property("margin-bottom", "1.25rem") } }) {
                Button({
                    onClick { showAdvanced = !showAdvanced }
                    style {
                        property("background", "transparent"); property("border", "none"); property("padding", "0")
                        property("color", "var(--rp-pine)"); property("font-weight", "700"); property("font-size", "0.85rem")
                        property("cursor", "pointer"); property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.4rem")
                    }
                }) {
                    I({ classes("fa-solid", if (showAdvanced) "fa-chevron-up" else "fa-sliders") })
                    Text(if (showAdvanced) "Dölj kodmappning & ID-inställningar" else "Anpassa lönearter / tidkoder & anställnings-ID")
                }

                if (showAdvanced) {
                    Div({
                        style {
                            property("margin-top", "0.85rem"); property("padding", "1.1rem")
                            property("background", "var(--rp-base)"); property("border-radius", "16px")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem")
                        }
                    }) {
                        Div({ style { property("display", "flex"); property("flex-wrap", "wrap"); property("gap", "1.25rem") } }) {
                            // Employee ID matching
                            Div {
                                Span({ style { property("display", "block"); property("font-size", "0.8rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)"); property("margin-bottom", "0.35rem") } }) {
                                    Text("Matcha anställda på")
                                }
                                Select({
                                    onChange { ev -> settings = settings.copy(employeeIdMode = ev.target.value) }
                                    style { fortnoxInputStyle() }
                                }) {
                                    Option("personal_number", { if (settings.employeeIdMode == "personal_number") selected() }) {
                                        Text("Personnummer (standard i Fortnox/Bokio)")
                                    }
                                    Option("employee_number", { if (settings.employeeIdMode == "employee_number") selected() }) {
                                        Text("Anställningsnummer (standard i Visma/Kontek)")
                                    }
                                }
                            }

                            // PAXml transaction type
                            if (settings.fileFormat == "paxml") {
                                Div {
                                    Span({ style { property("display", "block"); property("font-size", "0.8rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)"); property("margin-bottom", "0.35rem") } }) {
                                        Text("PAXml-transaktionstyp")
                                    }
                                    Select({
                                        onChange { ev -> settings = settings.copy(codeMode = ev.target.value) }
                                        style { fortnoxInputStyle() }
                                    }) {
                                        Option("tidkod", { if (settings.codeMode == "tidkod") selected() }) {
                                            Text("Tidtransaktioner (<tidtrans> / <tidkod>)")
                                        }
                                        Option("loneart", { if (settings.codeMode == "loneart") selected() }) {
                                            Text("Lönetransaktioner (<lonetrans> / <lonart>)")
                                        }
                                    }
                                }
                            }
                        }

                        // Code fields
                        Div({
                            style {
                                property("display", "grid")
                                property("grid-template-columns", "repeat(auto-fit, minmax(150px, 1fr))")
                                property("gap", "0.75rem")
                            }
                        }) {
                            codeInputField("Ordinarie tid", settings.codeNormal) { settings = settings.copy(codeNormal = it) }
                            codeInputField("Kvälls-OB (18-24)", settings.codeOb1) { settings = settings.copy(codeOb1 = it) }
                            codeInputField("Natt-OB (00-06)", settings.codeOb2) { settings = settings.copy(codeOb2 = it) }
                            codeInputField("Helg-OB (Lör-Sön)", settings.codeOb3) { settings = settings.copy(codeOb3 = it) }
                        }
                    }
                }
            }

            // Single Primary Export Button
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem"); property("flex-wrap", "wrap") } }) {
                Button({
                    onClick {
                        exporting = true
                        feedbackMsg = null
                        fortnoxScope.launch {
                            fortnoxRepo.exportPayroll(workplaceId, fromDate, toDate, settings).onSuccess { content ->
                                val ext = when (settings.fileFormat) {
                                    "paxml" -> "xml"
                                    "json" -> "json"
                                    else -> "csv"
                                }
                                val mime = when (settings.fileFormat) {
                                    "paxml" -> "application/xml"
                                    "json" -> "application/json"
                                    else -> "text/csv;charset=utf-8"
                                }
                                val filename = "${settings.targetSystem}_loneunderlag_${fromDate}_$toDate.$ext"
                                downloadFile(filename, content, mime)
                                feedbackIsError = false
                                feedbackMsg = "${activePreset.name} ($activeFormatLabel) exporterad ($filename)."
                            }.onFailure {
                                feedbackIsError = true
                                feedbackMsg = "Export misslyckades: ${it.message}"
                            }
                            exporting = false
                        }
                    }
                    if (exporting) disabled()
                    style {
                        property("padding", "0.85rem 1.75rem"); property("border", "none")
                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))"); property("color", "white")
                        property("border-radius", "50px"); property("font-weight", "800"); property("font-size", "0.98rem"); property("cursor", "pointer")
                        property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.6rem")
                        if (exporting) property("opacity", "0.7")
                    }
                }) {
                    I({ classes("fa-solid", "fa-file-arrow-down") })
                    Text(
                        if (exporting) "Exporterar..."
                        else "Exportera till ${activePreset.name} ($activeFormatLabel)"
                    )
                }
            }
        }

        // Dynamic Step-by-Step Guide Card
        Div({
            style {
                property("background", "var(--rp-info-bg)"); property("border-radius", "24px"); property("padding", "1.5rem")
                property("border", "2px solid var(--rp-info-bg)")
            }
        }) {
            H4({ style { property("margin", "0 0 0.75rem 0"); property("font-weight", "800"); property("color", "var(--rp-pine)"); property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                I({ classes("fa-solid", "fa-circle-info") })
                Text("Så importerar du till ${activePreset.name}")
            }
            Div({ style { property("color", "var(--rp-iris)"); property("font-size", "0.9rem"); property("line-height", "1.6") } }) {
                val steps = activePreset.steps(settings.fileFormat)
                steps.forEachIndexed { idx, stepText ->
                    P({ style { property("margin", if (idx == steps.lastIndex) "0" else "0 0 0.5rem 0") } }) {
                        Text("${idx + 1}. $stepText")
                    }
                }
            }
        }
    }
}

@Composable
private fun codeInputField(label: String, value: String, onValueChange: (String) -> Unit) {
    Div {
        Span({ style { property("display", "block"); property("font-size", "0.78rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)"); property("margin-bottom", "0.3rem") } }) {
            Text(label)
        }
        Input(InputType.Text) {
            value(value)
            onInput { onValueChange(it.value) }
            style {
                fortnoxInputStyle()
                property("width", "100%")
                property("box-sizing", "border-box")
            }
        }
    }
}

private fun downloadFile(filename: String, content: String, mimeType: String) {
    val blob = js("new Blob([content], {type: mimeType})")
    val url = js("URL.createObjectURL(blob)") as String
    val a = window.document.createElement("a")
    a.asDynamic().href = url
    a.asDynamic().download = filename
    window.document.body?.appendChild(a)
    a.asDynamic().click()
    window.document.body?.removeChild(a)
    js("URL.revokeObjectURL(url)")
}

private fun currentMonthStartFortnox(): String {
    val d = kotlin.js.Date()
    return "${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-01"
}

private fun currentTodayFortnox(): String {
    val d = kotlin.js.Date()
    return "${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}"
}

private fun org.jetbrains.compose.web.css.StyleScope.fortnoxInputStyle() {
    property("padding", "0.6rem 1rem"); property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "14px")
    property("font-size", "0.92rem"); property("font-weight", "600"); property("outline", "none")
    property("background", "var(--rp-surface)"); property("color", "var(--rp-text)")
}
